import { Box, CircularProgress } from '@mui/material';
import { FunctionComponent } from 'react';
import { BrowserRouter, Navigate, Route, Routes as RRDomRoutes } from 'react-router-dom';
import { BackgroundLogo } from './components/BackgroundLogo';
import { NavigationMenu } from './components/NavigationMenu';
import { useUnknownAuthState } from './contexts/AuthContext';
import { PageViewTracker } from './data/PageViewTracking';
import { AdminScreen } from './screens/admin/AdminScreen';
import { EditQuestionSetScreen } from './screens/admin/EditQuestionSetScreen';
import { QuestionSetsScreen } from './screens/admin/QuestionSetsScreen';
import { QuestionsScreen } from './screens/admin/QuestionsScreen';
import { AnimationScreen } from './screens/animation/AnimationScreen';
import { LoginScreen } from './screens/auth/LoginScreen';
import { AnswersScreen } from './screens/game/AnswersScreen';
import { GameFinishedScreen } from './screens/game/GameFinishedScreen';
import { GameplayScreen } from './screens/game/GameplayScreen';
import { QuestionCardScreen } from './screens/game/QuestionCardScreen';
import { GameSetupScreen } from './screens/game/GameSetupScreen';
import { GameWelcomeScreen } from './screens/game/GameWelcomeScreen';
import { HomeScreen } from './screens/HomeScreen';
import { SettingsScreen } from './screens/SettingsScreen';
import { Environment } from './util/Environment';

export const Routes: FunctionComponent = () => {
	const { isSignedIn, isAdmin, loading } = useUnknownAuthState();

	return (loading)
		? <LoadingScreen />
		: <BrowserRouter>
			<PageViewTracker />
			<NavigationMenu />
			<RRDomRoutes>
				{isSignedIn
					? <>
						{AuthenticatedRoutes()}
						{isAdmin && AdminRoutes()}
						{Environment.development && DevelopmentRoutes()}
					</>
					: UnauthenticatedRoutes()
				}
			</RRDomRoutes>
		</BrowserRouter>;
};

/** User is guaranteed to be signed in within this stack */
function AuthenticatedRoutes() {
	return <>
		<Route path="/" element={<HomeScreen />} />
		<Route path="/setup" element={<GameSetupScreen />} />
		<Route path="/welcome" element={<GameWelcomeScreen />} />
		<Route path="/game" element={<GameplayScreen />} />
		<Route path="/game-finished" element={<GameFinishedScreen />} />
		<Route path="/answers" element={<AnswersScreen />} />
		<Route path="/question-card" element={<QuestionCardScreen />} />
		<Route path="/settings" element={<SettingsScreen />} />
		<Route path="*" element={<Navigate to="/" replace />} />
	</>;
}

/** User is guaranteed to be an admin within this stack */
function AdminRoutes() {
	return <Route path="/admin" >
		<Route index element={<AdminScreen />} />
		<Route path="questions" element={<QuestionsScreen />} />
		<Route path="question-sets" element={<QuestionSetsScreen />} />
		<Route path="question-sets/:id" element={<EditQuestionSetScreen />} />
	</Route>;
}

/** Development-only routes */
function DevelopmentRoutes() {
	return <Route path="/animations" element={<AnimationScreen />} />;
}

/** User is guaranteed not to be signed in within this stack */
function UnauthenticatedRoutes() {
	return <>
		<Route path="/login" element={<LoginScreen />} />
		<Route path="*" element={<Navigate to="/login" replace />} />
	</>;
}

const LoadingScreen: FunctionComponent = () => <Box
	sx={{
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		minHeight: '100vh',
	}}
>
	<BackgroundLogo />
	<CircularProgress aria-label="Loading" />
</Box>;
